import React, { useEffect } from "react";
import { Modal, Form, Input, Switch, message } from "antd";
import { IRegisteredUserListResponse } from "../../../types/api/users-manage";
import { useUpdateRegisteredUser } from "../../../hooks/api/user/hooks";

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: IRegisteredUserListResponse | null;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ isOpen, onClose, user }) => {
  const [form] = Form.useForm();
  const updateMutation = useUpdateRegisteredUser();

  useEffect(() => {
    if (user && isOpen) {
      const nameParts = (user.name || "").trim().split(" ");
      const firstName = user.firstName || nameParts[0] || "";
      const lastName = user.lastName || nameParts.slice(1).join(" ") || "";

      form.setFieldsValue({
        firstName,
        lastName,
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
        status: user.status !== undefined ? user.status : true,
      });
    } else {
      form.resetFields();
    }
  }, [user, isOpen, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!user?._id) return;

      const computedName = `${values.firstName || ""} ${values.lastName || ""}`.trim();
      const payload = {
        ...values,
        name: computedName,
      };

      updateMutation.mutate(
        {
          id: user._id,
          data: payload,
        },
        {
          onSuccess: () => {
            message.success("User updated successfully");
            onClose();
          },
          onError: (err: any) => {
            message.error(
              err?.response?.data?.message || err?.message || "Failed to update user",
            );
          },
        },
      );
    } catch (err) {
      // validation errors
    }
  };

  return (
    <Modal
      title="Edit Registered User Details"
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={updateMutation.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" className="mt-4">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last Name">
            <Input placeholder="Last Name" />
          </Form.Item>
        </div>

        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true, message: "Please enter username" },
            { min: 3, message: "Username must be at least 3 characters" },
            {
              pattern: /^[a-zA-Z0-9_.-]+$/,
              message: "Username can only contain letters, numbers, underscores, hyphens, and dots",
            },
          ]}
        >
          <Input placeholder="Username" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email Address"
          rules={[
            { required: true, message: "Please enter email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="email@example.com" />
        </Form.Item>

        <Form.Item name="phone" label="Phone Number">
          <Input placeholder="+1 234 567 8900" />
        </Form.Item>

        <Form.Item name="status" label="Account Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserEditModal;
